import { contextStore } from "../auth/context-store.js";
import { defaultSystemSettings, isImageAllowed } from "../../domain/settings/system-settings.js";
import type { OperationalSettings } from "../../application/services/system-settings-service.js";

/**
 * O pedaço de CONFIGURAÇÕES que as telas de trabalho usam (autosave, limites
 * de arquivo, padrões de END). Carregado uma vez por sessão; sem conseguir
 * carregar, valem os PADRÕES — que são exatamente o comportamento de antes.
 *
 * Isto é conveniência de tela, não controle: o que precisa ser garantido
 * (numeração, obrigatoriedades de END na validação, política de senha,
 * aliases da importação) é aplicado no serviço.
 */
let cached: OperationalSettings | null = null;
let inflight: Promise<OperationalSettings> | null = null;

function defaults(): OperationalSettings {
  const d = defaultSystemSettings();
  return { general: d.general, files: d.files, end: d.end, inspection: { defaultNature: d.inspection.defaultNature, defaultModality: d.inspection.defaultModality } };
}

export async function loadOperationalSettings(force = false): Promise<OperationalSettings> {
  if (cached && !force) return cached;
  if (inflight && !force) return inflight;
  const c = contextStore.get();
  const ctx = { activeClientId: c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  inflight = (async () => {
    try {
      const service = window.app?.platform?.systemSettingsService;
      cached = service ? await service.getOperationalSettings(ctx as never) : defaults();
    } catch {
      cached = defaults();
    } finally {
      inflight = null;
    }
    return cached ?? defaults();
  })();
  return inflight;
}

export function operationalSettings(): OperationalSettings {
  return cached ?? defaults();
}

export function clearOperationalSettingsCache(): void {
  cached = null;
}

/** Espera do autosave em ms; `null` = autosave desligado em Configurações. */
export function autosaveDelayMs(): number | null {
  const g = operationalSettings().general;
  return g.autosaveEnabled ? Math.max(1, g.autosaveDelaySeconds) * 1000 : null;
}

/** Motivo da recusa de um arquivo de imagem pelas regras de Configurações → Arquivos, ou `null`. */
export function imageRejection(file: { name: string; type: string; size: number }): string | null {
  const f = operationalSettings().files;
  if (!isImageAllowed(f, file.name, file.type)) return `"${file.name}": tipo não permitido. Extensões aceitas: ${f.allowedImageExtensions.map(e => e.toUpperCase()).join(", ")} (Configurações → Arquivos).`;
  const max = f.maxImageMb * 1024 * 1024;
  if (file.size > max) return `"${file.name}" passa do limite de ${String(f.maxImageMb).replace(".", ",")} MB por imagem (Configurações → Arquivos).`;
  return null;
}

/** Quantas fotos ainda cabem no relatório (`Infinity` sem limite). */
export function remainingPhotoSlots(currentCount: number): number {
  const limit = operationalSettings().files.maxPhotosPerReport;
  return limit === null ? Number.POSITIVE_INFINITY : Math.max(0, limit - currentCount);
}
